import React from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  BellIcon,
  MicrophoneIcon,
  DocumentTextIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CogIcon,
  ChatBubbleLeftRightIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  PencilIcon,
  ChartBarIcon,
  ArrowPathIcon,
  EyeIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

export default function MeetingsHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <VideoCameraIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Meetings Management System</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive meeting lifecycle management from scheduling to completion.
            Create, track, attend, and document meetings with integrated alerts and AI-powered features.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">System Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <PlusIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Create Meetings</h3>
                <p className="text-gray-600">
                  Schedule training sessions, appraisals, client meetings, and team gatherings with automated logistics.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <CalendarDaysIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Calendar View</h3>
                <p className="text-gray-600">
                  Visual calendar interface to view, manage, and track all scheduled meetings with AI summaries.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                  <BellIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Smart Alerts</h3>
                <p className="text-gray-600">
                  Real-time notifications for upcoming meetings with one-click join functionality.
                </p>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-6 border border-cyan-200">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <VideoCameraIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Meeting Interface</h3>
                <p className="text-gray-600">
                  Interactive meeting environment with document viewing, progress tracking, and time management.
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <MicrophoneIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Voice Notes</h3>
                <p className="text-gray-600">
                  Real-time speech-to-text note taking with automatic saving and transcription features.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  <CogIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Integration</h3>
                <p className="text-gray-600">
                  Automated meeting summaries, attendance tracking, and intelligent scheduling assistance.
                </p>
              </div>
            </div>

            {/* System Flow Diagram */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">Meeting Lifecycle Flow</h3>
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mb-2">
                    <PlusIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Create</span>
                  <span className="text-xs text-gray-600">Schedule Meeting</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-full flex items-center justify-center mb-2">
                    <CalendarDaysIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Calendar</span>
                  <span className="text-xs text-gray-600">View & Manage</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full flex items-center justify-center mb-2">
                    <BellIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Alert</span>
                  <span className="text-xs text-gray-600">5-Min Warning</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-full flex items-center justify-center mb-2">
                    <VideoCameraIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Attend</span>
                  <span className="text-xs text-gray-600">Join Meeting</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-green-600 rounded-full flex items-center justify-center mb-2">
                    <MicrophoneIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Document</span>
                  <span className="text-xs text-gray-600">Voice Notes</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-2">
                    <CheckCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Complete</span>
                  <span className="text-xs text-gray-600">AI Summary</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Meeting Types */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Meeting Types & Features</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                      <DocumentTextIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Training Meetings</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Document-based training materials</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Progress tracking and completion certificates</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Interactive quizzes and assessments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Voice note integration for key takeaways</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                      <UserGroupIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Appraisal Meetings</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Performance review documentation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Goal setting and development planning</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Feedback collection and action items</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Automated follow-up scheduling</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mr-3">
                      <MapPinIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Client Meetings</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Travel time and distance calculations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Client information and project details</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Meeting outcome tracking</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Follow-up action item assignment</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-2xl p-6 border border-cyan-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-lg flex items-center justify-center mr-3">
                      <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Team Meetings</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Multi-attendee management</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Agenda and action item tracking</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Decision documentation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Automated meeting minutes generation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ArrowRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Complete Meeting Workflow</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-12">
              {/* Step 1: Create Meeting */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Create Your Meeting</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Start by scheduling a new meeting with all necessary details and automated logistics.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Meeting Details</h4>
                        <ul className="space-y-2 text-purple-700">
                          <li>• Select meeting type (Training, Appraisal, Client, Team)</li>
                          <li>• Set date, time, and duration</li>
                          <li>• Choose location or virtual meeting</li>
                          <li>• Add meeting title and description</li>
                        </ul>
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                        <h4 className="text-lg font-semibold text-indigo-900 mb-3">Attendee Management</h4>
                        <ul className="space-y-2 text-indigo-700">
                          <li>• Select staff members from your team</li>
                          <li>• Add external participants for client meetings</li>
                          <li>• Automatic notification system</li>
                          <li>• Attendance tracking integration</li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                        <h4 className="text-lg font-semibold text-blue-900 mb-3">Smart Features</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• Automatic travel time calculation</li>
                          <li>• Distance and route optimization</li>
                          <li>• Conflict detection with existing meetings</li>
                          <li>• Resource booking integration</li>
                        </ul>
                      </div>

                      <div className="bg-cyan-50 rounded-xl p-6 border border-cyan-200">
                        <h4 className="text-lg font-semibold text-cyan-900 mb-3">Content Preparation</h4>
                        <ul className="space-y-2 text-cyan-700">
                          <li>• Attach training documents or agendas</li>
                          <li>• Link to relevant projects or appraisals</li>
                          <li>• Set up pre-meeting materials</li>
                          <li>• Configure note-taking preferences</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Calendar Management */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Manage via Calendar</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    View all meetings in an intuitive calendar interface with advanced management features.
                  </p>

                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-8 border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">Calendar Features</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <EyeIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Monthly, weekly, and daily views</span>
                          </li>
                          <li className="flex items-start">
                            <EyeIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Color-coded meeting types</span>
                          </li>
                          <li className="flex items-start">
                            <EyeIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Quick meeting details on hover</span>
                          </li>
                          <li className="flex items-start">
                            <EyeIcon className="h-5 w-5 text-indigo-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Drag-and-drop rescheduling</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">Management Tools</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <CogIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Delete meetings with confirmation</span>
                          </li>
                          <li className="flex items-start">
                            <CogIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Bulk operations for multiple meetings</span>
                          </li>
                          <li className="flex items-start">
                            <CogIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Export calendar data</span>
                          </li>
                          <li className="flex items-start">
                            <CogIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">AI-powered meeting summaries</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Meeting Alerts */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Receive Smart Alerts</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Get notified 5 minutes before meetings start with real-time updates and quick actions.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
                      <div className="flex items-center mb-6">
                        <BellIcon className="h-8 w-8 text-blue-500 mr-3" />
                        <h4 className="text-xl font-semibold text-blue-900">Alert Features</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-blue-700">5-minute advance notifications</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-blue-700">Real-time WebSocket updates</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-blue-700">Browser notifications on desktop</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-blue-700">Mobile-optimized alert interface</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8 border border-cyan-200">
                      <div className="flex items-center mb-6">
                        <ArrowRightIcon className="h-8 w-8 text-cyan-500 mr-3" />
                        <h4 className="text-xl font-semibold text-cyan-900">Quick Actions</h4>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-cyan-200">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">Join Meeting</span>
                            <span className="text-sm text-green-600 font-semibold">One-Click</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Navigate directly to meeting interface</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-cyan-200">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">Dismiss Alert</span>
                            <span className="text-sm text-gray-600 font-semibold">Optional</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Hide notification without joining</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Attend Meeting */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Attend the Meeting</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Join the interactive meeting environment with document viewing, progress tracking, and time management.
                  </p>

                  <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-2xl p-8 border border-cyan-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <DocumentTextIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Document Viewer</h4>
                        <p className="text-sm text-gray-600">Navigate through training materials with progress tracking</p>
                      </div>

                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <ClockIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Time Tracking</h4>
                        <p className="text-sm text-gray-600">Real-time elapsed time display and meeting duration management</p>
                      </div>

                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <ChartBarIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Progress Monitor</h4>
                        <p className="text-sm text-gray-600">Visual progress bars and completion status indicators</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5: Voice Notes */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-teal-500 to-green-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Capture Voice Notes</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Use advanced speech-to-text technology to capture meeting discussions and key points in real-time.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-2xl p-8 border border-teal-200">
                      <div className="flex items-center mb-6">
                        <MicrophoneIcon className="h-8 w-8 text-teal-500 mr-3" />
                        <h4 className="text-xl font-semibold text-teal-900">Recording Features</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-teal-700">Continuous speech recognition</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-teal-700">Real-time transcription display</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-teal-700">Audio feedback for start/stop</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-teal-700">Recording duration tracking</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                      <div className="flex items-center mb-6">
                        <PencilIcon className="h-8 w-8 text-green-500 mr-3" />
                        <h4 className="text-xl font-semibold text-green-900">Note Management</h4>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Auto-Save</span>
                            <span className="text-sm text-green-600 font-semibold">Automatic</span>
                          </div>
                          <p className="text-sm text-gray-600">Notes saved to meeting record every few seconds</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Manual Save</span>
                            <span className="text-sm text-blue-600 font-semibold">On-Demand</span>
                          </div>
                          <p className="text-sm text-gray-600">Force save notes at any time</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Reset Function</span>
                            <span className="text-sm text-orange-600 font-semibold">Clear All</span>
                          </div>
                          <p className="text-sm text-gray-600">Start fresh with new transcription</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 6: Complete & Review */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">6</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Complete & Generate Summary</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    End the meeting and automatically generate AI-powered summaries with action items and key insights.
                  </p>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">Completion Features</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Automatic attendance recording</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Meeting duration tracking</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Document completion status</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Note finalization and saving</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">AI Summary Generation</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Key discussion points extraction</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Action items identification</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Decision documentation</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Follow-up task assignment</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-green-600 px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Technical Features & Integration</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-2xl p-6 border border-teal-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-green-600 rounded-lg flex items-center justify-center mr-3">
                    <ArrowPathIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Real-time Sync</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• WebSocket-based live updates</li>
                  <li>• Cross-device synchronization</li>
                  <li>• Instant notification delivery</li>
                  <li>• Real-time attendance tracking</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                    <SpeakerWaveIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Voice Technology</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Advanced speech recognition</li>
                  <li>• Multi-language support</li>
                  <li>• Noise cancellation</li>
                  <li>• Continuous transcription</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl p-6 border border-emerald-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-lg flex items-center justify-center mr-3">
                    <ChartBarIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">AI Analytics</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Automated summary generation</li>
                  <li>• Sentiment analysis</li>
                  <li>• Action item extraction</li>
                  <li>• Performance insights</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <MapPinIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Location Services</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• GPS-based travel calculations</li>
                  <li>• Route optimization</li>
                  <li>• Distance estimation</li>
                  <li>• Traffic-aware timing</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Mobile Optimization</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Touch-optimized interfaces</li>
                  <li>• Responsive design</li>
                  <li>• Offline capability</li>
                  <li>• Push notifications</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <ShieldCheckIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Security & Privacy</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• End-to-end encryption</li>
                  <li>• Secure WebSocket connections</li>
                  <li>• GDPR compliance</li>
                  <li>• Data retention policies</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting & Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Common Issues</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Microphone Not Working</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Check browser microphone permissions</li>
                        <li>• Ensure microphone is not muted</li>
                        <li>• Try refreshing the page</li>
                        <li>• Test microphone in browser settings</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Meeting Alerts Not Showing</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Enable browser notifications</li>
                        <li>• Check WebSocket connection</li>
                        <li>• Verify meeting time settings</li>
                        <li>• Clear browser cache</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3">Performance Tips</h3>
                  <ul className="space-y-2 text-sm text-yellow-700">
                    <li>• Close unnecessary browser tabs during meetings</li>
                    <li>• Use a stable internet connection</li>
                    <li>• Keep microphone volume moderate</li>
                    <li>• Save notes periodically during long meetings</li>
                    <li>• Use fullscreen mode for better focus</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Best Practices</h3>
                  <ul className="space-y-3 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Test microphone and speakers before meetings</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Set up meetings at least 24 hours in advance</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Review meeting materials beforehand</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use clear, descriptive meeting titles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Follow up on action items promptly</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3">Pro Tips</h3>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Use keyboard shortcuts for faster navigation</li>
                    <li>• Enable browser notifications for important meetings</li>
                    <li>• Keep meeting notes organized with timestamps</li>
                    <li>• Utilize AI summaries for quick catch-ups</li>
                    <li>• Schedule buffer time between meetings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Usage */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <DevicePhoneMobileIcon className="h-8 w-8 text-white mr-4" />
              <ComputerDesktopIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Platform-Specific Usage</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Mobile Experience</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h4 className="text-lg font-semibold text-blue-900 mb-3">Touch-Optimized Features</h4>
                    <ul className="space-y-2 text-blue-700">
                      <li>• Large, easy-to-tap buttons</li>
                      <li>• Swipe gestures for navigation</li>
                      <li>• Pinch-to-zoom for documents</li>
                      <li>• Voice note recording with touch controls</li>
                      <li>• Mobile-specific alert notifications</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                    <h4 className="text-lg font-semibold text-indigo-900 mb-3">iOS-Specific Features</h4>
                    <ul className="space-y-2 text-indigo-700">
                      <li>• Safari-optimized speech recognition</li>
                      <li>• iOS notification integration</li>
                      <li>• Automatic tab closure after 15 seconds</li>
                      <li>• iCloud sync for meeting notes</li>
                      <li>• Face ID/Touch ID integration</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Desktop Experience</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                    <h4 className="text-lg font-semibold text-purple-900 mb-3">Keyboard Shortcuts</h4>
                    <ul className="space-y-2 text-purple-700">
                      <li>• <kbd className="px-2 py-1 bg-purple-200 rounded text-xs">Space</kbd> - Start/Stop recording</li>
                      <li>• <kbd className="px-2 py-1 bg-purple-200 rounded text-xs">Tab</kbd> - Navigate between sections</li>
                      <li>• <kbd className="px-2 py-1 bg-purple-200 rounded text-xs">F11</kbd> - Toggle fullscreen</li>
                      <li>• <kbd className="px-2 py-1 bg-purple-200 rounded text-xs">Ctrl+S</kbd> - Save notes</li>
                      <li>• <kbd className="px-2 py-1 bg-purple-200 rounded text-xs">Esc</kbd> - Exit fullscreen</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-xl p-6 border border-pink-200">
                    <h4 className="text-lg font-semibold text-pink-900 mb-3">Browser Features</h4>
                    <ul className="space-y-2 text-pink-700">
                      <li>• Browser notification permissions</li>
                      <li>• Microphone access management</li>
                      <li>• Fullscreen meeting mode</li>
                      <li>• Multi-tab meeting handling</li>
                      <li>• Download meeting summaries</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}